/* eslint-disable */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import T from 'i18n-react';
import { FormControl, FormHelperText, TextField } from '@material-ui/core';

import TextMaskCustom from './TextMaskCustom';

import {
  DATE_MASK,
  YEAR_MASK,
  TIME_MASK,
  POSTAL_CODE_MASK,
  EMAIL_MASK,
  CURRENCY_MASK,
  PHONE_NUMBER_MASK,
  formatPhoneCountryCode,
  formatDateDifferent,
} from './util';

import classes from './TextMask.module.scss';

const InputMaskType = {
  POSTAL_CODE: 'postalCode',
  DATE: 'date',
  YEAR: 'year',
  TIME: 'time',
  PHONE: 'phone',
  EMAIL: 'email',
  CURRENCY: 'currency',
  CUSTOM: 'custom',
};

const placeholderCharDefault = '\u2000';
const systemDateFormat = 'YYYY-MM-DD';

function DateTextMask(props) {
  return <TextMaskCustom {...props} mask={DATE_MASK} />;
}

function YearTextMask(props) {
  return <TextMaskCustom {...props} mask={YEAR_MASK} />;
}

function TimeTextMask(props) {
  return <TextMaskCustom {...props} mask={TIME_MASK} />;
}

function PhoneNumberTextMask(props) {
  return <TextMaskCustom {...props} mask={PHONE_NUMBER_MASK} />;
}

function PostalCodeTextMask(props) {
  return <TextMaskCustom {...props} mask={POSTAL_CODE_MASK} />;
}

function EmailTextMask(props) {
  return <TextMaskCustom {...props} mask={EMAIL_MASK} />;
}

function CurrencyTextMask(props) {
  return <TextMaskCustom {...props} mask={CURRENCY_MASK} />;
}

const getTextMaskPlaceholderDefault = (mask) => {
  switch (mask) {
    case InputMaskType.DATE:
      return 'DD/MM/YYYY';
    case InputMaskType.YEAR:
      return 'YYYY';
    case InputMaskType.TIME:
      return 'HH:MM (GMT +X)';
    case InputMaskType.PHONE:
      return '[+XXX] [XXX-XXX-XXXX]';
    case InputMaskType.POSTAL_CODE:
      return 'XXXXX';
    case InputMaskType.EMAIL:
      return 'xxx@yyy.zzz';
    case InputMaskType.CURRENCY:
      return '$ XXX';
    default:
      return '';
  }
};

const TextMaskInput = (props) => {
  const {
    className,
    mask,
    pattern,
    format: displayFormat,
    name,
    value: srcValue,
    disabled,
    noError = true,
    placeholder,
    fieldName,
    shrink = true,
    label,
    variant = 'outlined',
    mandatory,
    fullWidth,
    storeName,
    accessPath,
    onChange,
    onBlur,
    errorText,
    touched,
    formProps,
    ...others
  } = props;

  const MyTextMask = (props) => {
    return <TextMaskCustom {...props} mask={pattern} />;
  };

  const getTextMaskComponent = (mask) => {
    switch (mask) {
      case InputMaskType.DATE:
        return DateTextMask;
      case InputMaskType.YEAR:
        return YearTextMask;
      case InputMaskType.TIME:
        return TimeTextMask;
      case InputMaskType.PHONE:
        return PhoneNumberTextMask;
      case InputMaskType.POSTAL_CODE:
        return PostalCodeTextMask;
      case InputMaskType.EMAIL:
        return EmailTextMask;
      case InputMaskType.CURRENCY:
        return CurrencyTextMask;
      case InputMaskType.CUSTOM:
        return MyTextMask;
      default:
        return null;
    }
  };

  const myFormatValue = (val) => {
    if (!val || val === 'NULL') return '';

    if (!val.includes(placeholderCharDefault) && mask === 'date')
      return formatDateDifferent(val, systemDateFormat, displayFormat);

    return val;
  };

  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(myFormatValue(srcValue));
  }, [srcValue, setValue, myFormatValue]);

  const myUpdateFunc = (event) => {
    // set value for display
    setValue(event.target.value);

    if (!event.target.value.includes(placeholderCharDefault) && mask === 'date') {
      event.target.value = formatDateDifferent(event.target.value, displayFormat, systemDateFormat);
    }

    if (onChange) onChange(event);
  };

  const myBlurFunc = (event) => {
    if (mask === 'phone') {
      event.target.value = formatPhoneCountryCode(event.target.value);

      if (onChange) onChange(event);
    }
    if (onBlur) onBlur(event);
  };

  const getLabel = (labelValue) => {
    if (!label) return null;

    if (labelValue) return labelValue;

    const customLabel =
      typeof accessPath[accessPath.length - 1] === 'string' ? (
        <div className={classes.labelDiv}>
          {mandatory ? <div className={classes.mandatory}>*</div> : null}
          {T.translate(`${storeName}.${accessPath[accessPath.length - 1]}`)}
        </div>
      ) : (
        <div className={classes.labelDiv}>
          {mandatory ? <div className={classes.mandatory}>*</div> : null}
          {T.translate(`${storeName}.${fieldName}`)}
        </div>
      );

    return customLabel;
  };

  const getPlaceholder = (maskVal) => {
    let text = getTextMaskPlaceholderDefault(maskVal);

    if (placeholder) {
      text = placeholder;
    }
    // else {
    //     text = T.translate(`${storeName}.${accessPath[accessPath.length - 1]}`);
    // }
    return text;
  };

  const getError = () => {
    if (touched) {
      return errorText;
    }
    return false;
  };

  return (
    <div
      className={clsx(
        classes.textInputWrapper,
        className,
        variant === 'outlined' && classes.variantOutlined,
        !label && classes.noLabel
      )}>
      <FormControl error={!!(touched && errorText)}>
        <TextField
          margin='dense'
          label={getLabel(label)}
          type='text'
          placeholder={getPlaceholder(mask)}
          variant={variant}
          disabled={disabled}
          error={getError()}
          InputProps={{
            inputComponent: getTextMaskComponent(mask),
            name,
            value,
            onChange: (e) => myUpdateFunc(e),
            onBlur: (e) => myBlurFunc(e),
            ...others,
          }}
          InputLabelProps={{ shrink }}
          fullWidth={fullWidth}
        />
        {!noError && (
          <FormHelperText
            className={clsx(
              classes.errorText,
              !!(touched && errorText) ? '' : classes.errorTextHide
            )}>
            {errorText}
          </FormHelperText>
        )}
      </FormControl>
    </div>
  );
};

TextMaskInput.propTypes = {};

TextMaskInput.propTypes = {
  mask: PropTypes.string.isRequired,
};

export default TextMaskInput;
